import { Injectable, signal, inject } from '@angular/core';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { IDocument } from '../../../interfaces/i-document';
import { IAgent } from '../../../interfaces/i-agents';
import { ISensitivityLabel } from '../../../interfaces/i-sensitivity-label';
import { UtilitiesService } from '../../../services/utilities.service';
import { IConversation } from "../interfaces/i-conversation";
import { ChatDocumentService } from './chat-document.service';
import { ChatSessionService } from './chat-session.service';
import { SensitivityLabelService } from '../../../services/sensitivity-label.service';
import { mapLegacySensitivityLabel, toISensitivityLabel } from '../../../utilities/sensitivity-label.utils';

@Injectable({
  providedIn: 'root'
})
export class ConversationStarterService {
  // State of whether we are in the Start Conversation page or not
  isStartConversation = signal(false);
  hasError = signal(false);
  errorMessages = signal<string[]>([]);
  /**
   * Tracks the IDs of agents that are incompatible with the currently selected
   * sensitivity label. Components can read this to highlight affected agent chips.
   * Reset to an empty Set whenever the error state is cleared.
   */
  incompatibleAgentIds = signal<Set<string>>(new Set());
  /**
   * Single source of truth for whether the user has explicitly chosen a
   * sensitivity label via the label picker.
   *
   * - Set to `true` inside `updateSensitivityLabel` (the only entry-point for
   *   user-driven label changes).
   * - Reset to `false` by `setSensitivityLabelByAgents` (system auto-derives
   *   the label) and when the component is destroyed or resets.
   *
   * Used by `onAgentSelected` to decide which validation path to take:
   * - `true`  → keep the user's label and re-validate agents against it via
   *             `updateSensitivityLabel`.
   * - `false` → auto-derive the best common label via `setSensitivityLabelByAgents`.
   */
  userHasChosenSensitivityLabel = signal<boolean>(false);
  // Hacky fix for file attachments being set to an empty array when leaving the Start Session page and creating a session
  // due to the ChatDocumentService clearing selected documents on session creation.
  // this is bc when we create a session, we do a check for if we have sessionId and if !isStartConversation, we use the sessionInputData map to set documents,
  // if it's empty, then we lose the selected documents.
  // once we set the sessionInputData map to use the ChatDocumentService selectedDocuments(), we can set the flag to false.
  isSessionCreatedFromStartConversation = signal(false);

  // Hacky fix for setting the sensitivity label on a newly created session when we have a File Upload Instruction from the Start Session page
  setSensitivityLabelByAttachedDocuments = signal(false);

  conversation = signal<IConversation | undefined>(undefined);


  #utilitiesService = inject(UtilitiesService);
  #sensitivityLabelService = inject(SensitivityLabelService);
  #chatDocumentService = inject(ChatDocumentService);
  #chatSessionService = inject(ChatSessionService);

  setSensitivityLabelByAgents(agents: IAgent[]) {
    // Reset the error state
    this.hasError.set(false);
    this.errorMessages.set([]);
    this.incompatibleAgentIds.set(new Set());
    this.userHasChosenSensitivityLabel.set(false);

    if (!Array.isArray(agents) || agents.length === 0) {
      return;
    }

    // Map each agent's backend ISensitivityLabel to GUID strings using the legacy mapper
    const labelGuidArrays: string[][] = agents.map(agent =>
      [...new Set(
        agent.details?.sensitivityLabels
          ?.map(label => mapLegacySensitivityLabel(label)?.id)
          .filter((id): id is string => !!id)
        ?? []
      )]
    );

    // Find the common GUID set (intersection across all agents)
    const commonGuids = labelGuidArrays.reduce((common, current) =>
      common.filter(guid => current.includes(guid))
    );

    // If there are common labels, select the lowest one by sensitivity
    if (commonGuids.length > 0) {
      commonGuids.sort((a, b) => {
        const orderA = this.#sensitivityLabelService.byCode().get(a)?.sensitivity ?? 0;
        const orderB = this.#sensitivityLabelService.byCode().get(b)?.sensitivity ?? 0;
        return orderA - orderB;
      });
      let lowestGuid = commonGuids[0];

      // If there are selected documents, ensure we do not set a lower SL than the current one
      if (this.#chatDocumentService.selectedDocuments().length > 0) {
        const currentSL = this.conversation()?.sensitivityLabel;
        const currentOrder = mapLegacySensitivityLabel(currentSL!)?.sensitivity ?? currentSL?.sensitivity ?? 0;

        let noCommonSensitivityLabelFound = true;
        for (const guid of commonGuids) {
          const guidOrder = this.#sensitivityLabelService.byCode().get(guid)?.sensitivity ?? 0;
          if (guidOrder >= currentOrder) {
            lowestGuid = guid;
            noCommonSensitivityLabelFound = false;
            break;
          } else {
            noCommonSensitivityLabelFound = true;
          }
        }

        if (noCommonSensitivityLabelFound) {
          this.hasError.set(true);
          this.errorMessages.update(errors => [...errors, `The selected Agent(s) has a Sensitivity Label that is lower than the currently attached document's Sensitivity Label.
            \nPlease either adjust the attached document(s) or your Agent(s) selection.`]);
          return;
        }
      }

      const labels = this.#utilitiesService.findLabelsByCodes([lowestGuid]);
      if (labels.length > 0) {
        this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: labels[0] } : convo);
        return;
      }
    } else {
      // No common denominator found — identify which agents are incompatible
      const labelFrequency = new Map<string, IAgent[]>();
      for (const agent of agents) {
        const agentLabelGuids = agent.details?.sensitivityLabels
          ?.map(l => mapLegacySensitivityLabel(l)?.id)
          .filter((guid): guid is string => !!guid) ?? [];
        for (const guid of agentLabelGuids) {
          labelFrequency.set(guid, [...(labelFrequency.get(guid) ?? []), agent]);
        }
      }

      const maxSharedCount = Math.max(
        ...Array.from(labelFrequency.values()).map(agentList => agentList.length)
      );

      const mostSharedLabelGuids = Array.from(labelFrequency.entries())
        .filter(([, agentList]) => agentList.length === maxSharedCount)
        .map(([guid]) => guid);

      const incompatibleAgents = agents.filter(agent => {
        const agentLabelGuids = agent.details?.sensitivityLabels
          ?.map(l => mapLegacySensitivityLabel(l)?.id)
          .filter((guid): guid is string => !!guid) ?? [];
        return !mostSharedLabelGuids.some(guid => agentLabelGuids.includes(guid));
      });

      if (incompatibleAgents.length > 0) {
        const agentNames = incompatibleAgents
          .map(a => a.details?.name ?? a.id)
          .join(', ');

        this.hasError.set(true);
        this.errorMessages.set([
          `The selected Agents do not have a common Sensitivity Label. The following Agent(s) are incompatible: ${agentNames}.`
        ]);
        this.incompatibleAgentIds.set(new Set(incompatibleAgents.map(a => a.id)));
      } else {
        // Fallback: all agents are somehow incompatible with each other
        const agentNames = agents.map(a => a.details?.name ?? a.id).join(', ');
        this.hasError.set(true);
        this.errorMessages.set([
          `The selected Agents do not have a common Sensitivity Label: ${agentNames}.`
        ]);
        this.incompatibleAgentIds.set(new Set(agents.map(a => a.id)));
      }
    }
  }

  /**
   * Sets the sensitivity label for the conversation based on the documents attached.
   * Finds the document with the highest sensitivity level and updates accordingly.
   */
  setSensitivityLabelByDocuments(documents: IDocument[]) {
    // Find the document with the highest SL sensitivity using legacy mapper
    const getDocOrder = (doc: IDocument): number =>
      mapLegacySensitivityLabel(doc.sensitivityLabel)?.sensitivity ?? doc.sensitivityLabel?.sensitivity ?? 0;

    const maxOrder = Math.max(...documents.map(getDocOrder));
    const highestSensitivityLabelDocument = documents.find(doc => getDocOrder(doc) === maxOrder);

    const docExpandedLabel = mapLegacySensitivityLabel(highestSensitivityLabelDocument?.sensitivityLabel);
    if (docExpandedLabel) {
      if (this.setSensitivityLabelByAttachedDocuments()) {
        const sessionSL = this.#chatSessionService.selectedSession()!.sensitivityLabels?.[0];
        const sessionOrder = mapLegacySensitivityLabel(sessionSL)?.sensitivity ?? sessionSL?.sensitivity ?? 0;
        if (docExpandedLabel && docExpandedLabel.sensitivity >= sessionOrder) {
          this.#chatSessionService.selectedSession()!.sensitivityLabels = [toISensitivityLabel(docExpandedLabel)];
        }
        return;
      }

      const conversationSL = this.conversation()?.sensitivityLabel;
      const conversationOrder = mapLegacySensitivityLabel(conversationSL!)?.sensitivity ?? conversationSL?.sensitivity ?? 0;

      if (this.isStartConversation() && docExpandedLabel && docExpandedLabel.sensitivity > conversationOrder) {
        this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: toISensitivityLabel(docExpandedLabel!) } : convo);
      }
    }
  }

  setSensitivityLabelByInstruction(instruction: IInstructions) {
    const labels = instruction.details?.sensitivityLabels!;

    this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: labels[0] } : convo);
  }

  /**
   * Updates the sensitivity label for the conversation after validating against selected agents and documents.
   * @param label The new sensitivity label to set (backend ISensitivityLabel format).
   * @param agents The list of selected agents to validate against.
   */
  updateSensitivityLabel(label: ISensitivityLabel, agents: IAgent[]) {
    this.userHasChosenSensitivityLabel.set(true);

    // When the sensitivity-label feature is disabled the picker is hidden and no compatibility
    // constraints apply, so skip agent/document validation and clear any prior errors.
    if (!this.#sensitivityLabelService.enabled()) {
      this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: label } : convo);
      this.hasError.set(false);
      this.errorMessages.set([]);
      this.incompatibleAgentIds.set(new Set());
      return;
    }

    const newErrors: string[] = [];
    const incompatibleAgents: IAgent[] = [];

    // Check if label is allowed by all selected agents.
    // Compare by GUID first (backward-compatible with legacy integer-id transition).
    if (Array.isArray(agents) && agents.length > 0) {
      for (const agent of agents) {
        const isAllowed = agent.details?.sensitivityLabels?.some(l =>
          mapLegacySensitivityLabel(l)?.id === (mapLegacySensitivityLabel(label)?.id ?? label.code)
        ) ?? false;
        if (!isAllowed) {
          incompatibleAgents.push(agent);
        }
      }

      if (incompatibleAgents.length > 0) {
        const agentNames = incompatibleAgents.map(a => a.details?.name ?? a.id).join(', ');
        newErrors.push(
          `The selected Sensitivity Label is not compatible with the following Agent(s): ${agentNames}.\n`
        );
      }
    }

    this.incompatibleAgentIds.set(new Set(incompatibleAgents.map(a => a.id)));

    // Check if label is valid for attached documents using order values
    const documents = this.#chatDocumentService.selectedDocuments();
    if (documents.length > 0) {
      const labelExpanded = mapLegacySensitivityLabel(label);
      const labelOrder = labelExpanded?.sensitivity ?? label.sensitivity ?? 0;

      const maxDocOrder = Math.max(
        ...documents.map(doc => {
          const docExpanded = mapLegacySensitivityLabel(doc.sensitivityLabel);
          const effectiveOrder = docExpanded?.parent
            ? (this.#sensitivityLabelService.byCode().get(docExpanded.parent)?.sensitivity ?? docExpanded.sensitivity)
            : (docExpanded?.sensitivity ?? doc.sensitivityLabel?.sensitivity ?? 0);
          return effectiveOrder;
        })
      );

      if (labelOrder < maxDocOrder) {
        newErrors.push(`The selected Sensitivity Label is lower than the currently attached document's Sensitivity Label.\n`);
      }
    }

    this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: label } : convo);

    if (newErrors.length > 0) {
      this.hasError.set(true);
      this.errorMessages.set(newErrors);
    } else {
      this.hasError.set(false);
      this.errorMessages.set([]);
      this.incompatibleAgentIds.set(new Set());
    }
  }
}
